import { useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Td,
  Text,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import DocumentList from 'components/documents/DocumentList';
import useDisableTruck from 'domains/admin/hooks/useDisableTruck';
import useApproveTruck from 'domains/admin/hooks/useApproveTruck';
import useRejectTruck from 'domains/admin/hooks/useRejectTruck';
import OwnerInfoCard from '../PendingTrucks/OwnerInfoCard';
import { useTranslation } from 'i18n/TranslationProvider';

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString();
};

const getStatusBadge = (status, t) => {
  switch (status) {
    case 'approved':
      return <Badge colorScheme="green">{t('Approved')}</Badge>;
    case 'pending':
      return <Badge colorScheme="orange">{t('Pending')}</Badge>;
    case 'rejected':
      return <Badge colorScheme="red">{t('Rejected')}</Badge>;
    case 'disabled':
      return <Badge colorScheme="gray">{t('Disabled')}</Badge>;
    default:
      return <Badge>{t('Unknown')}</Badge>;
  }
};

const ownerLabel = (owner) =>
  owner?.companyName || owner?.displayName || owner?.email || '-';

const AllTrucksItem = ({ truck, refetch, tableMode = false }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { disableTruck, loading } = useDisableTruck();
  const { approveTruck, loading: approveLoading } = useApproveTruck();
  const { rejectTruck, loading: rejectLoading } = useRejectTruck();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [approvalDate, setApprovalDate] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const docsModal = useDisclosure();
  const cancelRef = useRef();
  const approveCancelRef = useRef();
  const rejectCancelRef = useRef();
  const todayIso = new Date().toISOString().split('T')[0];

  const panelBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(13,23,53,0.72)');
  const panelBorder = useColorModeValue('rgba(226, 232, 240, 0.9)', 'rgba(255,255,255,0.14)');
  const mutedText = useColorModeValue('gray.600', 'gray.300');

  const handleDisable = async () => {
    try {
      await disableTruck(truck.id || truck._id);
      refetch?.();
    } catch (err) {
      console.error('Failed to disable truck', err);
    } finally {
      setIsAlertOpen(false);
    }
  };

  const handleApprove = async () => {
    if (!approvalDate) {
      toast({
        title: t('CMR expiry date is required'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await approveTruck(truck.id || truck._id, {
        cmrInsuranceExpiryDate: approvalDate,
      });
      setIsApproveOpen(false);
      setApprovalDate('');
      refetch?.();
      toast({
        title: t('Truck approved successfully'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: t('Failed to approve truck'),
        description: err?.response?.data?.message || err?.message,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  const handleReject = async () => {
    const reason = rejectionReason.trim();
    if (!reason) {
      toast({
        title: t('Rejection reason is required'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await rejectTruck(truck.id || truck._id, {
        rejectionReason: reason,
      });
      setIsRejectOpen(false);
      setRejectionReason('');
      refetch?.();
      toast({
        title: t('Truck rejected successfully'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: t('Failed to reject truck'),
        description: err?.response?.data?.message || err?.message,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  const actions = (
    <HStack spacing={2} flexWrap="wrap">
      <Button
        size="xs"
        variant="outline"
        colorScheme="blue"
        onClick={tableMode ? docsModal.onOpen : () => setShowDocuments((v) => !v)}
      >
        {tableMode ? t('Documents') : showDocuments ? t('Hide documents') : t('View documents')}
      </Button>
      <Button
        size="xs"
        colorScheme="green"
        variant="outline"
        onClick={() => setIsApproveOpen(true)}
        isDisabled={truck.validationStatus !== 'pending' || approveLoading || rejectLoading}
      >
        {t('Approve')}
      </Button>
      <Button
        size="xs"
        colorScheme="red"
        variant="outline"
        onClick={() => setIsRejectOpen(true)}
        isDisabled={truck.validationStatus !== 'pending' || approveLoading || rejectLoading}
      >
        {t('Reject')}
      </Button>
      <Button
        size="xs"
        colorScheme="red"
        variant="outline"
        isLoading={loading}
        isDisabled={truck.validationStatus === 'disabled'}
        onClick={() => setIsAlertOpen(true)}
      >
        {t('Disable')}
      </Button>
    </HStack>
  );

  return (
    <>
      {tableMode ? (
        <Tr>
          <Td>
            <Text fontWeight="700">{truck.licensePlate}</Text>
          </Td>
          <Td>
            <Stack spacing={0}>
              <Text>{ownerLabel(truck.ownerId)}</Text>
              {truck.ownerId?.email ? (
                <Text fontSize="xs" color={mutedText}>
                  {truck.ownerId.email}
                </Text>
              ) : null}
            </Stack>
          </Td>
          <Td>{getStatusBadge(truck.validationStatus, t)}</Td>
          <Td>{formatDate(truck.cmrInsuranceExpiryDate) || t('Not set')}</Td>
          <Td>
            {truck.hasConfirmedCmr === false ? (
              <Badge colorScheme="orange">{t('Missing')}</Badge>
            ) : (
              <Badge colorScheme="green">{t('Uploaded')}</Badge>
            )}
          </Td>
          <Td>
            {truck.cmrLastRejectedReason ? (
              <Tooltip hasArrow label={truck.cmrLastRejectedReason}>
                <Text maxW="240px" noOfLines={1} color="red.400">
                  {truck.cmrLastRejectedReason}
                </Text>
              </Tooltip>
            ) : (
              <Text color={mutedText}>-</Text>
            )}
          </Td>
          <Td>{actions}</Td>
        </Tr>
      ) : (
        <Card
          p={5}
          mb={4}
          borderRadius="20px"
          bg={panelBg}
          border="1px solid"
          borderColor={panelBorder}
          boxShadow="0 16px 34px rgba(15, 28, 63, 0.12)"
        >
          <Stack spacing={4}>
            <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} flexWrap="wrap" gap={3}>
              <Box>
                <Text fontWeight="700" fontSize="lg">
                  {truck.licensePlate}
                </Text>
                <HStack mt={1} spacing={2}>
                  {getStatusBadge(truck.validationStatus, t)}
                  <Text fontSize="sm" color={mutedText}>
                    {t('CMR expiry')}: {formatDate(truck.cmrInsuranceExpiryDate) || t('Not set')}
                  </Text>
                </HStack>
                {truck.cmrLastRejectedReason && (
                  <Text mt={2} fontSize="sm" color="red.400">
                    {t('Last rejection reason')}: {truck.cmrLastRejectedReason}
                  </Text>
                )}
              </Box>

              {actions}
            </Flex>

            {truck.ownerId && <OwnerInfoCard owner={truck.ownerId} />}

            <Collapse in={showDocuments} animateOpacity>
              <Box mt={2}>
                <DocumentList
                  entityType="truck"
                  entityId={truck._id || truck.id}
                  docType="cmr"
                />
              </Box>
            </Collapse>
          </Stack>
        </Card>
      )}

      <Modal isOpen={docsModal.isOpen} onClose={docsModal.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('Truck documents')} - {truck.licensePlate}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <DocumentList
              entityType="truck"
              entityId={truck._id || truck.id}
              docType="cmr"
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsAlertOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Disable Truck')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('Are you sure you want to disable this truck? This action cannot be undone.')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDisable}
                ml={3}
                isLoading={loading}
              >
                {t('Disable')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isApproveOpen}
        leastDestructiveRef={approveCancelRef}
        onClose={() => setIsApproveOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Approve truck?')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Stack spacing={3}>
                <Text color={mutedText}>
                  {t('Set CMR insurance expiry date to approve this truck.')}
                </Text>
                <FormControl isRequired>
                  <FormLabel>{t('CMR insurance expiry date')}</FormLabel>
                  <Input
                    type="date"
                    value={approvalDate}
                    onChange={(event) => setApprovalDate(event.target.value)}
                    min={todayIso}
                  />
                </FormControl>
              </Stack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={approveCancelRef} onClick={() => setIsApproveOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button
                colorScheme="green"
                ml={3}
                isLoading={approveLoading}
                onClick={handleApprove}
              >
                {t('Approve')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isRejectOpen}
        leastDestructiveRef={rejectCancelRef}
        onClose={() => setIsRejectOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Reject truck?')}
            </AlertDialogHeader>

            <AlertDialogBody>
              <Stack spacing={3}>
                <Text color={mutedText}>
                  {t('Provide a rejection reason for this CMR review.')}
                </Text>
                <FormControl isRequired>
                  <FormLabel>{t('Rejection reason')}</FormLabel>
                  <Textarea
                    value={rejectionReason}
                    onChange={(event) => setRejectionReason(event.target.value)}
                    placeholder={t('Explain why this CMR was rejected')}
                    minH="96px"
                  />
                </FormControl>
              </Stack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={rejectCancelRef} onClick={() => setIsRejectOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={rejectLoading}
                onClick={handleReject}
              >
                {t('Reject')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AllTrucksItem;
